import {
    Button,
    Grid,
    makeStyles,
    Theme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import DashboardClient from 'clients/dashboard-client'
import Spinner from 'common/spinner'
import { InvoicePeriodStatistic } from 'models/invoice-period-statistic'
import React from 'react'
import { saveAs } from 'file-saver'
import { tr } from 'utils/translations/translate'
import { isCurrentPeriod } from './dashboard-helper'
import { ProductTable } from './period-products-table'
import { AppState } from 'redux/app/app-store'
import { useDispatch, useSelector } from 'react-redux'
import {
    TEXT_AT,
    TEXT_DATE_SENT,
    TEXT_GENERATE_DIFF_FILE,
    TEXT_INVOICES_LAST_TRANSFERRED_ON,
    TEXT_NOT_SENT_PRODUCTS,
    TEXT_PERIOD_TITLE_CREDITING,
    TEXT_PERIOD_TITLE_NEW_INVOICES,
    TEXT_SEND_TO,
    TEXT_SEND_TO_ERP,
    TEXT_SENT_BY,
    TEXT_SENT_PRODUCTS,
    TEXT_SENT_SUM,
    TEXT_UNSENT_SUM,
} from 'utils/translations/keys'
import { toDecimalWithCurrency } from 'pages/contract-page/contract-helper'
import { ErpTransferStatisticLine } from 'models/erp-transfer-statistic'
import { toDateTimeFormat, toShortDate } from 'utils/time/timeUtil'
import { getInvoicingSystemName, isApiSystem } from 'common/invoicing-systems/invoicing-system-helper'
import { Restore } from '@material-ui/icons'

const useStyles = makeStyles((theme: Theme) => {
    return {
        container: {
            margin: '8px',
        },
        header: {
            padding: '0 62px 0 24px',
        },
        detailsHeader: {
            paddingRight: '38px',
        },
        numericColumn: {
            textAlign: 'right',
        },
        expansionPanel: {
            borderRadius: '0!important',
            boxShadow: 'none',
            background: 'transparent',
            margin: '6px 0 0 0',
            '&::before': {
                display: 'none',
            },
            '&.Mui-expanded': {
                margin: '6px 0 0 0',
            },
            '& > .MuiAccordionSummary-root': {
                background: 'white',
                '&.Mui-expanded': {
                    minHeight: 'initial',
                    margin: 'initial',
                },
                '& > .MuiAccordionSummarySummary-content.Mui-expanded': {
                    margin: 0,
                },
            },
            '& > .MuiCollapse-root': {
                marginTop: '2px',
                background: 'white',
            },
        },
        tableRow: {
            '&:last-child td': {
                borderBottom: 0,
            },
            '&:hover': {
                cursor: 'pointer',
            },
        },
        sendToErp: {
            marginTop: '1rem',
        },
        subtotals: {
            borderTop: '1px #f0f0f0 solid',
            paddingTop: '0.25rem',
            marginTop: '0.25rem',
        },
        periodSectionTitle: {
            paddingBottom: '1rem',
            color: 'Gray',
        },
        actionItems: {
            alignContent: 'center',
        },
        resetPeriodItem: {
            alignContent: 'center',
            flexGrow: 1
        }
    }
})

export const Period: React.FC<{
    period: InvoicePeriodStatistic
    index: number
    handleSendToErpDialogOpen: any
    setIsLoading: any
    isResendEnabled?: boolean
    refreshStatistics: any
    isLoading: boolean
    generateDiffFile: any
    showGenerateError: boolean
    showErpTransferStatistic: boolean
    erpTransferStatistic: ErpTransferStatisticLine[]
    isErpTransferRunning: boolean
    openInvoiceSummaryDialog: any
    resetPeriod: any
    generateInvoices: any
}> = (props) => {
    const styles = useStyles()
    const {
        period,
        index,
        handleSendToErpDialogOpen,
        setIsLoading,
        isResendEnabled,
        refreshStatistics,
        isLoading,
        generateDiffFile,
        showGenerateError,
        showErpTransferStatistic,
        erpTransferStatistic,
        isErpTransferRunning,
        openInvoiceSummaryDialog,
        resetPeriod,
        generateInvoices,
    } = props
    const splitProductsView = !!period.productsInvoiced && period.productsInvoiced.length > 0
    const hasRefunds = !!period.productsRefunded && period.productsRefunded.length > 0
    const hasUserInvoices = !!period.userInvoicesStatistic && period.userInvoicesStatistic.length > 0
    const showInvoiceSubtotals = true
    const showRefundSubtotals = true
    const dispatch = useDispatch()

    const downloadErrorReport = async (statistic: InvoicePeriodStatistic) => {
        setIsLoading(true)
        const fileResult = await DashboardClient.getErrorReport(statistic.year, statistic.period)
        saveAs(fileResult, `${statistic.year}-${statistic.period}_transfer-errors.csv`)
        setIsLoading(false)
    }

    const configuration = useSelector((state: AppState) => state.billingConfiguration.billingConfig)
    const isGodMode = useSelector((state: AppState) => state.auth.user?.godMode)

    const getReportDisabled = (period: InvoicePeriodStatistic) => {
        return period.invoiceCount === 0
    }

    const sendToErpDisabled = (period: InvoicePeriodStatistic) => {
        return (
            isErpTransferRunning ||
            period.invoiceCount === null ||
            period.invoiceCount === 0 ||
            ((period.invoicesNotTransferred === null || period.invoicesNotTransferred === 0) && !isResendEnabled)
        )
    }

    const erpTransferStatisticFiltered = erpTransferStatistic.filter(
        (x) => x.period === period.period && x.year === period.year,
    )

    const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)
    const invoicingSystemName = getInvoicingSystemName(tenantConfig?.invoiceSystem)
    const sendToErpText = isApiSystem(tenantConfig?.invoiceSystem)
        ? `${tr(TEXT_SEND_TO)} ${invoicingSystemName}`
        : tr(TEXT_SEND_TO_ERP)

    return (
        <Accordion
            className={styles.expansionPanel}
            defaultExpanded={isCurrentPeriod(period)}
            key={index}
            disabled={isErpTransferRunning}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid container>
                    <Grid item xs={1}>
                        {period.period}
                    </Grid>
                    <Grid item xs={1}>
                        {!!period.startDate ? toShortDate(period.startDate) : '-'}
                    </Grid>
                    <Grid item xs={2}>
                        {!!period.endDate ? toShortDate(period.endDate) : '-'}
                    </Grid>
                    <Grid item xs={2} className={styles.numericColumn}>
                        {period.invoiceCount}
                    </Grid>
                    <Grid item xs={2} className={styles.numericColumn}>
                        {toDecimalWithCurrency(period.invoiceNetTotal ?? 0)}
                    </Grid>
                    <Grid item xs={2} className={styles.numericColumn}>
                        {toDecimalWithCurrency(period.invoiceVatTotal ?? 0)}
                    </Grid>
                    <Grid item xs={2} className={styles.numericColumn}>
                        {toDecimalWithCurrency(period.invoiceTotal ?? 0)}
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                {period.invoiceCount !== undefined && period.invoiceCount > 0 && (
                    <div style={{ width: '100%' }}>
                        {splitProductsView && (
                            <>
                                <div className={`${hasRefunds ? 'mb-3' : ''}`}>
                                    <ProductTable
                                        products={period.productsInvoiced || []}
                                        isRefund={false}
                                        showSubtotals={showInvoiceSubtotals}
                                    />
                                </div>
                                {hasRefunds && (
                                    <>
                                        <div className={styles.periodSectionTitle}>
                                            {tr(TEXT_PERIOD_TITLE_CREDITING)}
                                        </div>
                                        <div>
                                            <ProductTable
                                                products={period.productsRefunded || []}
                                                isRefund={true}
                                                showSubtotals={showRefundSubtotals}
                                            />
                                        </div>
                                    </>
                                )}
                                {hasUserInvoices && (
                                    <>
                                        <div className={styles.periodSectionTitle}>
                                            {tr(TEXT_PERIOD_TITLE_NEW_INVOICES)}
                                        </div>
                                        <div>
                                            <ProductTable
                                                products={period.userInvoicesStatistic || []}
                                                isRefund={false}
                                                showSubtotals={true}
                                            />
                                        </div>
                                    </>
                                )}
                                {showErpTransferStatistic && erpTransferStatisticFiltered.length > 0 && (
                                    <>
                                        <Grid
                                            container
                                            className={styles.detailsHeader}
                                            style={{ marginBottom: '0.5rem', marginTop: '1rem' }}
                                        >
                                            <Grid item xs={2}>
                                                <strong>{tr(TEXT_DATE_SENT)}</strong>
                                            </Grid>
                                            <Grid item xs={2}>
                                                <strong>{tr(TEXT_SENT_BY)}</strong>
                                            </Grid>
                                            <Grid item xs={2} className={styles.numericColumn}>
                                                <strong>{tr(TEXT_SENT_PRODUCTS)}</strong>
                                            </Grid>
                                            <Grid item xs={2} className={styles.numericColumn}>
                                                <strong>{tr(TEXT_NOT_SENT_PRODUCTS)}</strong>
                                            </Grid>
                                            <Grid item xs={2} className={styles.numericColumn}>
                                                <strong>{tr(TEXT_SENT_SUM)}</strong>
                                            </Grid>
                                            <Grid item xs={2} className={styles.numericColumn}>
                                                <strong>{tr(TEXT_UNSENT_SUM)}</strong>
                                            </Grid>
                                        </Grid>

                                        {erpTransferStatisticFiltered.map((statistic, index: number) => (
                                            <Grid container className={styles.detailsHeader} key={statistic.id}>
                                                <Grid item xs={2}>
                                                    {toShortDate(statistic.transferDate)}
                                                </Grid>
                                                <Grid item xs={2}>
                                                    {statistic.createdByName}
                                                </Grid>
                                                <Grid item xs={2} className={styles.numericColumn}>
                                                    {statistic.productsSent}
                                                </Grid>
                                                <Grid item xs={2} className={styles.numericColumn}>
                                                    {statistic.productsNotSent}
                                                </Grid>
                                                <Grid item xs={2} className={styles.numericColumn}>
                                                    {toDecimalWithCurrency(statistic.totalAmountSent)}
                                                </Grid>
                                                <Grid item xs={2} className={styles.numericColumn}>
                                                    {toDecimalWithCurrency(statistic.totalAmountNotSent)}
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                        {!splitProductsView && (
                            <>
                                <div>
                                    <ProductTable products={period.products} isRefund={false} showSubtotals={false} />
                                </div>
                            </>
                        )}
                        <Grid container justifyContent="flex-end" className={styles.sendToErp} spacing={1}>
                            {isLoading ? (
                                <Spinner />
                            ) : (
                                <>
                                    {isGodMode && period.transferDate && (
                                        <>
                                            <Grid item className={styles.resetPeriodItem}>
                                                <Grid
                                                    container
                                                    justifyContent="flex-start"
                                                    spacing={1}
                                                >
                                                    <Grid item className={styles.actionItems}>
                                                        {`${tr(TEXT_INVOICES_LAST_TRANSFERRED_ON)} ${toDateTimeFormat(period.transferDate, tr(TEXT_AT))}`}
                                                    </Grid>
                                                    <Grid item className={styles.actionItems}>
                                                        <IconButton onClick={() => resetPeriod(period)}>
                                                            <Restore />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}

                                    {/* {isGodMode && (
                                        <>
                                            <Grid item>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={isErpTransferRunning}
                                                    onClick={() => generateInvoices(period)}
                                                >
                                                    {tr('TEXT_GENERATE_INVOICES_FOR_PERIOD')}
                                                </Button>
                                            </Grid>
                                        </>
                                    )} */}
                                    {configuration.isDiffFilesEnabled && (
                                        <Grid item className={styles.actionItems}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => generateDiffFile(period)}
                                                disabled={isErpTransferRunning}
                                            >
                                                {tr(TEXT_GENERATE_DIFF_FILE)}
                                            </Button>
                                        </Grid>
                                    )}
                                    {showGenerateError && erpTransferStatisticFiltered.length > 0 && (
                                        <Grid item className={styles.actionItems}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={isErpTransferRunning || getReportDisabled(period)}
                                                onClick={() => downloadErrorReport(period)}
                                            >
                                                {tr('TEXT_DOWNLOAD_ERROR_REPORT')}
                                            </Button>
                                        </Grid>
                                    )}
                                    <Grid item className={styles.actionItems}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={isErpTransferRunning || getReportDisabled(period)}
                                            onClick={() => openInvoiceSummaryDialog(period)}
                                        >
                                            {tr('TEXT_DOWNLOAD_INVOICE_SUMMARY')}
                                        </Button>
                                    </Grid>
                                    <Grid item className={styles.actionItems}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={isErpTransferRunning}
                                            onClick={() => refreshStatistics(period)}
                                        >
                                            {tr('TEXT_REFRESH_PERIOD_STATISTICS')}
                                        </Button>
                                    </Grid>
                                    <Grid item className={styles.actionItems}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={sendToErpDisabled(period)}
                                            onClick={() => handleSendToErpDialogOpen(period)}
                                        >
                                            {sendToErpText} (
                                            {!props.isResendEnabled
                                                ? (period.invoicesNotTransferred ?? 0)
                                                : (period.invoiceCount ?? 0)}
                                            )
                                        </Button>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </div>
                )}
                {period.invoiceCount === 0 && (
                    <div style={{ display: 'flex', flexGrow: 4 }}>
                        <Grid container justifyContent="flex-end" className={styles.sendToErp} spacing={1}>
                            {isLoading ? (
                                <Spinner />
                            ) : (
                                <>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => refreshStatistics(period)}
                                        >
                                            {tr('TEXT_REFRESH_PERIOD_STATISTICS')}
                                        </Button>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </div>
                )}
            </AccordionDetails>
        </Accordion>
    )
}
